/**
 * Fase-3 modal: homoniem-link-suggesties.
 *
 * Bij branch-switch levert de reconcile een lijst paren: een branch-local
 * concept dat (type + naam) matcht met een parent/main-concept maar een losse
 * logische identiteit heeft (zie `detectHomonymCandidatesForBranch` in de
 * worker). Per paar kiest de modelleur:
 *   - **Koppelen** (link) → adopteer de parent-identiteit op het branch-concept
 *     (`origin_id`); branch-divergentie blijft volledig heel. Re-hydrate zodat
 *     geërfde parent-relaties via de endpoint-remap zichtbaar worden;
 *   - **Negeren** (dismiss) → "écht twee verschillende dingen"; permanent op
 *     deze branch onthouden zodat de suggestie niet terugkomt;
 *   - **Later** → modal sluiten zonder beslissen; komt bij de volgende
 *     branch-switch terug.
 *
 * State leeft in de UI-store (`homonymPrompts`), gevuld door `hydrate`.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import { getRegistry, resolveBilingual } from "../registry";
import { useSession } from "../state/session";
import { type HomonymPrompt, useUi } from "../state/ui";
import { useWorkspaceData } from "../state/workspace";

export function HomonymLinkPromptModal() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const prompts = useUi((s) => s.homonymPrompts);
  const setPrompts = useUi((s) => s.setHomonymPrompts);
  const wsId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  const [busy, setBusy] = useState<string | null>(null);

  if (prompts.length === 0 || !wsId || !branchId) return null;

  const reg = getRegistry();
  const typeLabel = (typeId: string): string => {
    const et = reg.getElementType(typeId);
    return (et ? resolveBilingual(et.label, lang) : "") || typeId;
  };
  const promptKey = (p: HomonymPrompt): string => `${p.branchConceptId}|${p.parentConceptId}`;
  const nameLabel = (p: HomonymPrompt): string =>
    resolveBilingual(p.name, lang) || typeLabel(p.typeId);

  const resolve = async (p: HomonymPrompt, action: "link" | "dismiss") => {
    const key = promptKey(p);
    setBusy(key);
    try {
      const body = { branchConceptId: p.branchConceptId, parentConceptId: p.parentConceptId };
      if (action === "link") {
        await api.linkHomonym(wsId, branchId, body);
      } else {
        await api.dismissHomonym(wsId, branchId, body);
      }
      const remaining = prompts.filter((x) => promptKey(x) !== key);
      setPrompts(remaining);
      // Na een link verschijnen de geërfde parent-relaties via de
      // endpoint-remap — herhydrateer zodra het laatste item verwerkt is.
      if (remaining.length === 0 && action === "link") {
        await useWorkspaceData.getState().hydrate(wsId, branchId);
      }
    } catch {
      // Stil — bij een fout blijft het item staan zodat de modelleur 't
      // opnieuw kan proberen.
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="ledger fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-lg rounded-lg border border-(--ld-control) bg-(--ld-surface) p-5 shadow-xl">
        <h2 className="font-semibold text-base text-(--ld-text)">{t("homonymLink.title")}</h2>
        <p className="mt-1 text-(--ld-muted) text-sm">{t("homonymLink.description")}</p>
        <ul className="mt-4 flex max-h-80 flex-col gap-2 overflow-y-auto">
          {prompts.map((p) => (
            <li key={promptKey(p)} className="rounded border border-(--ld-border) p-3">
              <div className="font-medium text-(--ld-text) text-sm">
                {nameLabel(p)}{" "}
                <span className="font-normal text-(--ld-muted)">({typeLabel(p.typeId)})</span>
              </div>
              <div className="mt-0.5 text-(--ld-muted) text-xs">{t("homonymLink.matchHint")}</div>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  disabled={busy === promptKey(p)}
                  className="rounded border border-transparent bg-(--ld-positive-bg) px-3 py-1 font-medium text-(--ld-positive-ink) text-sm hover:bg-(--ld-positive-bg) disabled:opacity-50"
                  onClick={() => resolve(p, "link")}
                >
                  {t("homonymLink.link")}
                </button>
                <button
                  type="button"
                  disabled={busy === promptKey(p)}
                  className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-1 text-(--ld-text) text-sm hover:bg-(--ld-raised) disabled:opacity-50"
                  onClick={() => resolve(p, "dismiss")}
                >
                  {t("homonymLink.dismiss")}
                </button>
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-1 text-(--ld-text) text-sm hover:bg-(--ld-raised)"
            onClick={() => setPrompts([])}
          >
            {t("homonymLink.later")}
          </button>
        </div>
      </div>
    </div>
  );
}
